import { useCallback, useState, type JSX } from "react";

import Form from "@ant-design/compatible/es/form";
import { useAjaxManager } from "@impero-com/common/utils/ajax";
import { t } from "@impero-com/common/utils/i18n";
import { ButtonGroup, ButtonSpec, FormItem, Tooltip } from "@impero-com/ui";

import {
    InitialAssignmentKind,
    SubsequentAssignmentKind,
    NotificationRecipientKind,
    UserGroupView,
    UserOverview,
    EntityView,
} from "api/generated/model";
import { Drawer } from "components/drawer";
import { EditableUserTagListPlusInput } from "components/form/fields/editableUserTagListPlus";
import { ControlAssignmentGroup } from "utils/controls/assignmentUtils";
import { useFeatureFlag } from "utils/hooks/useFeatureFlag";
import { useOrgUserProfileCookie } from "utils/profileCookie";

import { useAvailableActions } from "../../../utils/hooks/useAvailableActions";
import { useOrgDisplayInfo } from "../../../utils/queries/useOrgDisplayInfoSuspenseQuery";
import { computeCreateSod } from "../editAssignmentsTable";
import { ControlAssignmentResources } from "../types";

import { EntitySection } from "./EntitySection";
import { NotifyOnControlCompletionSection } from "./NotifyOnControlCompletionSection";
import { isSameRecipient } from "./NotifyOnControlCompletionUtils";
import { ResponsibleSection } from "./responsible";
import { FirstReviewerSection, SecondReviewerSection } from "./reviewer";
import * as theme from "./theme.css";

export type DrawerCloseAction = "save" | "remove" | "reset";

export interface AssignmentDrawerProps {
    segregationOfDutiesEnabled: boolean;
    resources: ControlAssignmentResources;
    onUserAdded: (user: UserOverview) => void;
    onUserGroupAdded: (userGroup: UserGroupView) => void;
    onClose: (action: DrawerCloseAction) => void;
    onChange: (group: ControlAssignmentGroup) => void;
    type: "CREATE" | "EDIT";
    value: ControlAssignmentGroup;
    controlProgramId: number | null;
    entitiesById: Map<number, EntityView>;
}

export const AssignmentDrawer = ({
    type,
    value: assignmentData,
    onChange: dispatchAssignmentDataRaw,
    onClose,
    resources,
    segregationOfDutiesEnabled,
    onUserAdded,
    onUserGroupAdded,
    controlProgramId,
    entitiesById,
}: AssignmentDrawerProps): JSX.Element => {
    const dispatchAssignmentData = (
        fn: (current: ControlAssignmentGroup) => ControlAssignmentGroup
    ) => {
        dispatchAssignmentDataRaw(fn(assignmentData));
    };
    const ajaxManager = useAjaxManager();
    const orgDisplayInfo = useOrgDisplayInfo();
    const orgProfileCookie = useOrgUserProfileCookie();
    const availableActions = useAvailableActions();
    const [entityError, setEntityError] = useState<string | undefined>(
        undefined
    );
    const handleEntityValidationChange = useCallback(
        (error: string | undefined) => {
            setEntityError(error);
        },
        []
    );

    const handleEntityChanged = useCallback(
        (entityId: number | undefined) => {
            dispatchAssignmentDataRaw({
                ...assignmentData,
                entityId,
            });
        },
        [dispatchAssignmentDataRaw, assignmentData]
    );

    if (!orgProfileCookie) {
        throw new Error("User must be logged in to access profile cookie");
    }

    const canCreateTag = availableActions.userTag.create;
    const controlEntitiesEnabled = useFeatureFlag("controlEntities");

    const sod = computeCreateSod(
        assignmentData.initialAssignment,
        assignmentData.firstReviewer,
        assignmentData.secondReviewer,
        segregationOfDutiesEnabled
    );
    const defaultClaimantError = sod?.defaultClaimantError
        ? t(
              "CreateControl_Assignees_Sod_DefaultClaimantShouldBeDifferentFromReviewers"
          )
        : undefined;
    const firstReviewerError = sod?.firstReviewerError
        ? t(
              "CreateControl_Assignees_Sod_ReviewerShouldBeDifferentFromResponsible"
          )
        : undefined;
    const secondReviewerError = sod?.secondReviewerError
        ? t(
              sod.secondReviewerError ===
                  "SecondReviewerShouldBeDifferentFromResponsible"
                  ? "CreateControl_Assignees_Sod_SecondReviewerShouldBeDifferentFromResponsible"
                  : "CreateControl_Assignees_Sod_SecondReviewerShouldBeDifferentFromReviewer"
          )
        : undefined;

    const tagIds = assignmentData.tagIds ?? [];

    const entityTagIds =
        controlEntitiesEnabled && assignmentData.entityId !== undefined
            ? (entitiesById.get(assignmentData.entityId)?.tagIds ?? [])
            : [];

    const handleTagsChange = (newTagIds: string[]) => {
        dispatchAssignmentDataRaw({
            ...assignmentData,
            tagIds: newTagIds,
        });
    };

    const handleResponsibleChanged = (assigneeKind: InitialAssignmentKind) => {
        dispatchAssignmentData((assignmentData) => {
            return {
                ...assignmentData,
                initialAssignment: assigneeKind,
            };
        });
    };

    const handleFirstReviewerChanged = (
        firstReviewerKind: SubsequentAssignmentKind
    ) => {
        dispatchAssignmentData((assignmentData) => {
            return {
                ...assignmentData,
                firstReviewer: firstReviewerKind,
            };
        });
    };

    const handleFirstReviewerRemoved = () => {
        dispatchAssignmentData((assignmentData) => {
            if (!assignmentData.firstReviewer) {
                throw new Error(
                    "Cannot remove the first reviewer when there is no reviewer"
                );
            }
            if (assignmentData.secondReviewer) {
                throw new Error(
                    "Cannot remove the first reviewer when there are two reviewers"
                );
            }
            return {
                ...assignmentData,
                firstReviewer: undefined,
            };
        });
    };

    const handleSecondReviewerChanged = (
        secondReviewerKind: SubsequentAssignmentKind
    ) => {
        dispatchAssignmentData((assignmentData) => {
            if (!assignmentData.firstReviewer) {
                throw new Error(
                    "Cannot update the second reviewer of an assignee who does not have a reviewer"
                );
            }
            return {
                ...assignmentData,
                secondReviewer: secondReviewerKind,
            };
        });
    };

    const handleSecondReviewerRemoved = () => {
        dispatchAssignmentData((assignmentData) => {
            if (!assignmentData.firstReviewer) {
                throw new Error(
                    "Cannot remove the second reviewer when there is no reviewer"
                );
            }
            if (!assignmentData.secondReviewer) {
                throw new Error(
                    "Cannot remove the second reviewer when there is a single reviewer"
                );
            }
            return {
                ...assignmentData,
                secondReviewer: undefined,
            };
        });
    };

    const handleNotificationRecipientChanged = (
        recipientsKind: NotificationRecipientKind
    ) => {
        dispatchAssignmentData((assignmentData) => {
            const currentRecipients =
                assignmentData.notificationRecipients?.["AllCompleted"] ?? [];
            return {
                ...assignmentData,
                notificationRecipients: {
                    ...assignmentData.notificationRecipients,
                    AllCompleted: [...currentRecipients, recipientsKind],
                },
            };
        });
    };

    const handleNotificationRecipientRemoved = (
        recipientsKind: NotificationRecipientKind
    ) => {
        dispatchAssignmentData((assignmentData) => {
            const currentRecipients =
                assignmentData.notificationRecipients?.["AllCompleted"] ?? [];

            const updatedRecipients = currentRecipients.filter(
                (r) => !isSameRecipient(r, recipientsKind)
            );
            return {
                ...assignmentData,
                notificationRecipients:
                    updatedRecipients.length > 0
                        ? {
                              ...assignmentData.notificationRecipients,
                              AllCompleted: updatedRecipients,
                          }
                        : {},
            };
        });
    };

    const handleCancel = () => {
        if (type === "CREATE") {
            onClose("remove");
        } else {
            onClose("reset");
        }
    };

    const handleSaveButtonClick = () => {
        if (!entityError) {
            onClose("save");
        }
    };

    const buttons: ButtonSpec[] = [
        {
            id: "add-assignment-button",
            label:
                type === "CREATE"
                    ? t("CreateControl_Assignees_Drawer_AddButton")
                    : t("CreateControl_Assignees_Drawer_SaveButton"),
            type: "primary",
            disabled: sod !== undefined || !!entityError,
            onClick: handleSaveButtonClick,
        },
        {
            id: "drawer-cancel-id",
            label: t("Cancel"),
            type: "link",
            onClick: handleCancel,
        },
    ];

    return (
        <Drawer
            onCancel={handleCancel}
            buildHeader={() => t("CreateControl_Assignees_Drawer_Title")}
            buildFooter={() => (
                <Tooltip disabled={!entityError} title={entityError}>
                    <ButtonGroup buttonSpecs={buttons} />
                </Tooltip>
            )}
            maskClosable={false}
        >
            <Form>
                <FormItem className={theme.noBottomMargin}>
                    <ResponsibleSection
                        resources={resources}
                        availableActions={availableActions}
                        ajaxManager={ajaxManager}
                        orgCookie={orgDisplayInfo}
                        orgProfileCookie={orgProfileCookie}
                        value={assignmentData.initialAssignment}
                        onResponsibleChanged={handleResponsibleChanged}
                        onUserAdded={onUserAdded}
                        onUserGroupAdded={onUserGroupAdded}
                        defaultClaimantError={defaultClaimantError}
                    />
                </FormItem>
                <FormItem className={theme.noBottomMargin}>
                    <FirstReviewerSection
                        resources={resources}
                        availableActions={availableActions}
                        ajaxManager={ajaxManager}
                        orgCookie={orgDisplayInfo}
                        orgProfileCookie={orgProfileCookie}
                        value={assignmentData.firstReviewer}
                        onFirstReviewerChanged={handleFirstReviewerChanged}
                        onFirstReviewerRemoved={
                            !assignmentData.secondReviewer &&
                            !!assignmentData.firstReviewer
                                ? handleFirstReviewerRemoved
                                : undefined
                        }
                        onUserAdded={onUserAdded}
                        onUserGroupAdded={onUserGroupAdded}
                        error={firstReviewerError}
                    />
                </FormItem>
                <FormItem className={theme.noBottomMargin}>
                    {assignmentData.firstReviewer ? (
                        <SecondReviewerSection
                            type="EDIT"
                            resources={resources}
                            availableActions={availableActions}
                            ajaxManager={ajaxManager}
                            orgCookie={orgDisplayInfo}
                            orgProfileCookie={orgProfileCookie}
                            value={assignmentData.secondReviewer}
                            onSecondReviewerChanged={
                                handleSecondReviewerChanged
                            }
                            onSecondReviewerRemoved={
                                assignmentData.secondReviewer
                                    ? handleSecondReviewerRemoved
                                    : undefined
                            } // Because SecondReviewerSection disables the removal button when no callback is passed
                            onUserAdded={onUserAdded}
                            onUserGroupAdded={onUserGroupAdded}
                            error={secondReviewerError}
                        />
                    ) : (
                        <SecondReviewerSection type="NOT_AVAILABLE" />
                    )}
                </FormItem>
                {controlEntitiesEnabled && (
                    <FormItem className={theme.noBottomMargin}>
                        <EntitySection
                            controlProgramId={controlProgramId}
                            entityId={assignmentData.entityId}
                            onEntityChanged={handleEntityChanged}
                            onValidationChange={handleEntityValidationChange}
                        />
                    </FormItem>
                )}
                <FormItem className={theme.noBottomMargin}>
                    <EditableUserTagListPlusInput
                        label={t("Control_Assignment_Tags")}
                        disabled={false}
                        canCreateTag={canCreateTag}
                        selectedTagIds={tagIds}
                        readOnlyTagIds={entityTagIds}
                        setSelectedTagIds={handleTagsChange}
                    />
                </FormItem>
                <NotifyOnControlCompletionSection
                    notificationRecipients={
                        assignmentData.notificationRecipients?.[
                            "AllCompleted"
                        ] ?? []
                    }
                    onNotificationRecipientChanged={
                        handleNotificationRecipientChanged
                    }
                    onNotificationRecipientRemoved={
                        handleNotificationRecipientRemoved
                    }
                    onUserAdded={onUserAdded}
                    onUserGroupAdded={onUserGroupAdded}
                />
            </Form>
        </Drawer>
    );
};
